/**
 * ItTicketTypesPage.jsx
 *
 * Admin interface for managing IT ticket types used by the ITSM module.
 * IT ticket types drive SLA tracking, ticket reference prefixes, CSAT workflows,
 * and diagnostics tab visibility throughout the application.
 *
 * Features:
 * - View all IT ticket types with active/inactive filter
 * - Create new types with key, display name, description, prefix, icon, and color
 * - Edit existing types
 * - Deactivate/reactivate types
 * - System types (seeded) cannot be deleted
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import '@/styles/DepartmentManagement.scss';
import { FaEdit, FaInbox, FaPlus, FaSave, FaHeadset, FaSpinner, FaTimes, FaPause, FaPlay, FaSearch, FaTrashAlt, FaLock } from 'react-icons/fa';
import * as Sentry from '@sentry/react';
import IconPicker from '@/components/Common/IconPicker';
import { FaIcon } from '@/utils/faIconMap';

const COLOR_OPTIONS = [
    '#3b82f6', // Blue
    '#10b981', // Green
    '#ef4444', // Red
    '#f59e0b', // Orange
    '#8b5cf6', // Purple
    '#1abc9c', // Teal
    '#1f2937', // Dark Gray
    '#e91e63', // Pink
    '#00bcd4', // Cyan
    '#f97316'  // Amber
];

const ItTicketTypesPage = () => {
    const { apiFetch } = useAuth();

    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showInactive, setShowInactive] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const [showModal, setShowModal] = useState(false);
    const [editingType, setEditingType] = useState(null);
    const [saving, setSaving] = useState(false);

    const [formData, setFormData] = useState({
        typeKey: '',
        displayName: '',
        description: '',
        ticketPrefix: 'IT',
        icon: 'fa-headset',
        color: '#3b82f6',
        sortOrder: 0
    });

    const [errors, setErrors] = useState({});

    const fetchTypes = useCallback(async () => {
        try {
            setLoading(true);
            const response = await apiFetch(`/api/ITTicketTypes?includeInactive=${showInactive}`);
            const data = await response.json();
            setTypes(Array.isArray(data) ? data : []);
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching IT ticket types:', error);
            toast.error('Error loading IT ticket types');
        } finally {
            setLoading(false);
        }
    }, [apiFetch, showInactive]);

    useEffect(() => {
        fetchTypes();
    }, [fetchTypes]);

    const filteredTypes = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        const source = !q
            ? types
            : types.filter(t =>
                (t.typeKey || '').toLowerCase().includes(q)
                || (t.displayName || '').toLowerCase().includes(q)
                || (t.description || '').toLowerCase().includes(q)
            );
        return [...source].sort((a, b) => {
            const sortDiff = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
            if (sortDiff !== 0) return sortDiff;
            return (a.displayName || '').localeCompare(b.displayName || '');
        });
    }, [types, searchQuery]);

    const stats = useMemo(() => ({
        total: types.length,
        active: types.filter(t => t.isActive).length,
        inactive: types.filter(t => !t.isActive).length
    }), [types]);

    const handleFieldChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.typeKey?.trim()) newErrors.typeKey = 'Type key is required';
        if (!formData.displayName?.trim()) newErrors.displayName = 'Display name is required';
        if (!formData.ticketPrefix?.trim()) newErrors.ticketPrefix = 'Ticket prefix is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleCreateNew = () => {
        setEditingType(null);
        setFormData({
            typeKey: '',
            displayName: '',
            description: '',
            ticketPrefix: 'IT',
            icon: 'fa-headset',
            color: '#3b82f6',
            sortOrder: types.length + 1
        });
        setErrors({});
        setShowModal(true);
    };

    const handleEdit = (ticketType) => {
        setEditingType(ticketType);
        setFormData({
            typeKey: ticketType.typeKey,
            displayName: ticketType.displayName,
            description: ticketType.description || '',
            ticketPrefix: ticketType.ticketPrefix || 'IT',
            icon: ticketType.icon || 'fa-headset',
            color: ticketType.color || '#3b82f6',
            sortOrder: ticketType.sortOrder ?? 0
        });
        setErrors({});
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingType(null);
        setFormData({
            typeKey: '',
            displayName: '',
            description: '',
            ticketPrefix: 'IT',
            icon: 'fa-headset',
            color: '#3b82f6',
            sortOrder: 0
        });
        setErrors({});
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setSaving(true);
        try {
            const payload = {
                typeKey: formData.typeKey,
                displayName: formData.displayName,
                description: formData.description,
                ticketPrefix: formData.ticketPrefix,
                icon: formData.icon,
                color: formData.color,
                sortOrder: formData.sortOrder
            };

            let response;
            if (editingType) {
                response = await apiFetch(`/api/ITTicketTypes/${editingType.id}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            } else {
                response = await apiFetch('/api/ITTicketTypes', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });
            }

            const data = await response.json();

            if (response.ok) {
                toast.success(editingType ? 'IT ticket type updated' : 'IT ticket type created');
                handleCloseModal();
                fetchTypes();
            } else {
                toast.error(data.message || 'Failed to save IT ticket type');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error saving IT ticket type:', error);
            toast.error('Error saving IT ticket type');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (ticketType) => {
        try {
            const response = await apiFetch(`/api/ITTicketTypes/${ticketType.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    typeKey: ticketType.typeKey,
                    displayName: ticketType.displayName,
                    description: ticketType.description,
                    ticketPrefix: ticketType.ticketPrefix,
                    icon: ticketType.icon,
                    color: ticketType.color,
                    sortOrder: ticketType.sortOrder,
                    isActive: !ticketType.isActive
                })
            });

            if (response.ok) {
                toast.success(ticketType.isActive ? 'Type deactivated' : 'Type reactivated');
                fetchTypes();
            } else {
                const data = await response.json();
                toast.error(data.message || 'Failed to update type');
            }
        } catch (error) {
            Sentry.captureException(error);
            toast.error('Error updating type');
        }
    };

    const handleDelete = async (ticketType) => {
        if (ticketType.isSystem) {
            toast.warning('System types cannot be deleted');
            return;
        }
        if (!window.confirm(`Delete "${ticketType.displayName}"? This cannot be undone.`)) return;

        try {
            const response = await apiFetch(`/api/ITTicketTypes/${ticketType.id}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                toast.success('IT ticket type deleted');
                fetchTypes();
            } else {
                const data = await response.json();
                toast.error(data.message || 'Failed to delete type');
            }
        } catch (error) {
            Sentry.captureException(error);
            toast.error('Error deleting type');
        }
    };

    const renderTypeCard = (ticketType) => (
        <div
            key={ticketType.id}
            className={`department-card ${!ticketType.isActive ? 'inactive' : ''}`}
            style={{ borderLeftColor: ticketType.color || '#3b82f6' }}
        >
            <div className="department-card-header">
                <div className="department-icon" style={{ color: ticketType.color || '#3b82f6' }}>
                    <FaIcon icon={ticketType.icon || 'fa-headset'} size={24} />
                </div>
                <div className="department-info">
                    <h3>
                        {ticketType.displayName}
                        {ticketType.isSystem && (
                            <FaLock style={{ marginLeft: 6, fontSize: 12, color: '#6b7280' }} title="System type — cannot be deleted" />
                        )}
                    </h3>
                    <p className="department-description" style={{ fontFamily: 'monospace', fontSize: '0.8em', color: '#6b7280' }}>
                        {ticketType.typeKey}
                    </p>
                    {ticketType.description ? (
                        <p className="department-description">{ticketType.description}</p>
                    ) : (
                        <p className="department-description muted">No description provided</p>
                    )}
                </div>
            </div>

            <div className="department-meta-row">
                <span className="sort-order">Prefix: <strong>{ticketType.ticketPrefix}</strong></span>
                <span className="sort-order">Sort: {ticketType.sortOrder ?? 0}</span>
                {!ticketType.isActive && (
                    <span className="badge badge-secondary">Inactive</span>
                )}
            </div>

            <div className="department-actions">
                <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => handleEdit(ticketType)}
                    title="Edit type"
                >
                    <FaEdit /> Edit
                </button>
                <button
                    className={`btn btn-sm ${ticketType.isActive ? 'btn-outline-warning' : 'btn-outline-success'}`}
                    onClick={() => handleToggleActive(ticketType)}
                    title={ticketType.isActive ? 'Deactivate' : 'Activate'}
                >
                    {ticketType.isActive ? <FaPause /> : <FaPlay />}
                    {ticketType.isActive ? 'Deactivate' : 'Reactivate'}
                </button>
                {!ticketType.isSystem && (
                    <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(ticketType)}
                        title="Delete type"
                    >
                        <FaTrashAlt /> Delete
                    </button>
                )}
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="department-management loading-state">
                <div className="loading-spinner">
                    <FaSpinner className="fa-spin fa-2x" />
                    <p>Loading IT ticket types...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="department-management">
            <div className="page-header">
                <div className="header-left">
                    <h1>
                        <FaHeadset /> IT Ticket Types
                    </h1>
                    <p className="page-description">
                        Manage IT ticket types used by the ITSM module. Each type drives SLA tracking,
                        ticket reference prefixes, CSAT workflows, and diagnostics tab visibility.
                        System types cannot be deleted but can be deactivated.
                    </p>
                </div>
                <div className="header-actions">
                    <button className="btn btn-primary" onClick={handleCreateNew}>
                        <FaPlus /> Create Type
                    </button>
                </div>
            </div>

            <div className="department-overview-panel">
                <div className="department-stats-row">
                    <div className="stat-card">
                        <span className="stat-value">{stats.total}</span>
                        <span className="stat-label">Total</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-value stat-active">{stats.active}</span>
                        <span className="stat-label">Active</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-value stat-inactive">{stats.inactive}</span>
                        <span className="stat-label">Inactive</span>
                    </div>
                </div>

                <div className="filter-bar">
                    <div className="search-wrap">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search ticket types..."
                            aria-label="Search ticket types"
                        />
                    </div>

                    <label className="checkbox-label">
                        <input
                            type="checkbox"
                            checked={showInactive}
                            onChange={(e) => setShowInactive(e.target.checked)}
                        />
                        <span>Show Inactive Types</span>
                    </label>

                    <span className="department-count">
                        {filteredTypes.length} shown
                    </span>
                </div>
            </div>

            <div className="departments-grid">
                {filteredTypes.length === 0 ? (
                    <div className="no-departments">
                        <FaInbox className="fa-3x" />
                        <p>No IT ticket types found for the current filters.</p>
                        <button className="btn btn-primary" onClick={handleCreateNew}>
                            <FaPlus /> Create Type
                        </button>
                    </div>
                ) : (
                    filteredTypes.map(t => renderTypeCard(t))
                )}
            </div>

            {showModal && (
                <div className="modal-backdrop" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>
                                {editingType ? <FaEdit /> : <FaPlus />}
                                {editingType ? 'Edit IT Ticket Type' : 'Create IT Ticket Type'}
                            </h2>
                            <button className="close-btn" onClick={handleCloseModal}>
                                <FaTimes />
                            </button>
                        </div>

                        <div className="modal-body">
                            <div className="form-group">
                                <label htmlFor="typeKey">
                                    Type Key<span className="required">*</span>
                                </label>
                                <input
                                    id="typeKey"
                                    type="text"
                                    className={`form-control ${errors.typeKey ? 'is-invalid' : ''}`}
                                    value={formData.typeKey}
                                    onChange={(e) => handleFieldChange('typeKey', e.target.value)}
                                    placeholder="e.g., IT-NewCategory"
                                    disabled={!!editingType}
                                />
                                {errors.typeKey && <div className="invalid-feedback">{errors.typeKey}</div>}
                                <small className="help-text">Unique identifier. Cannot be changed after creation.</small>
                            </div>

                            <div className="form-group">
                                <label htmlFor="displayName">
                                    Display Name<span className="required">*</span>
                                </label>
                                <input
                                    id="displayName"
                                    type="text"
                                    className={`form-control ${errors.displayName ? 'is-invalid' : ''}`}
                                    value={formData.displayName}
                                    onChange={(e) => handleFieldChange('displayName', e.target.value)}
                                    placeholder="e.g., New Category"
                                />
                                {errors.displayName && <div className="invalid-feedback">{errors.displayName}</div>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="description">Description</label>
                                <textarea
                                    id="description"
                                    className="form-control"
                                    value={formData.description}
                                    onChange={(e) => handleFieldChange('description', e.target.value)}
                                    placeholder="Optional description of this ticket type"
                                    rows={3}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="ticketPrefix">
                                    Ticket Prefix<span className="required">*</span>
                                </label>
                                <input
                                    id="ticketPrefix"
                                    type="text"
                                    className={`form-control ${errors.ticketPrefix ? 'is-invalid' : ''}`}
                                    value={formData.ticketPrefix}
                                    onChange={(e) => handleFieldChange('ticketPrefix', e.target.value.toUpperCase())}
                                    placeholder="e.g., IT, CHG, REQ"
                                    maxLength={5}
                                />
                                {errors.ticketPrefix && <div className="invalid-feedback">{errors.ticketPrefix}</div>}
                                <small className="help-text">Used for ticket reference generation (e.g., IT-00001)</small>
                            </div>

                            <div className="form-row">
                                <div className="form-group half">
                                    <label>Icon</label>
                                    <IconPicker
                                        value={formData.icon}
                                        onChange={(icon) => handleFieldChange('icon', icon)}
                                        color={formData.color}
                                    />
                                </div>

                                <div className="form-group half">
                                    <label>Color</label>
                                    <div className="color-picker">
                                        {COLOR_OPTIONS.map(color => (
                                            <button
                                                key={color}
                                                type="button"
                                                className={`color-option ${formData.color === color ? 'selected' : ''}`}
                                                style={{ backgroundColor: color }}
                                                onClick={() => handleFieldChange('color', color)}
                                                title={color}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="sortOrder">Sort Order</label>
                                <input
                                    id="sortOrder"
                                    type="number"
                                    className="form-control"
                                    value={formData.sortOrder}
                                    onChange={(e) => handleFieldChange('sortOrder', parseInt(e.target.value) || 0)}
                                    min="0"
                                />
                                <small className="help-text">Lower numbers appear first in lists</small>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleCloseModal}
                                disabled={saving}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? (
                                    <><FaSpinner className="fa-spin" /> Saving...</>
                                ) : (
                                    <><FaSave /> {editingType ? 'Update' : 'Create'}</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ItTicketTypesPage;
